/**
 * Ce fichier prend les données fournies par l'utilisateur et les fichiers CSV et les utilise pour effectuer les calculs demandés.
 */
import type { 
  ResistanceParoi, 
  ResistanceIsolant, 
  RendementChauffage, 
  ParoiData, 
  RecommandationTravaux,
  FormDataInterface,
  TypeComposant,
  TypeTravaux,
  TypeIsolant,
  TypeVitrage,
  TypeMur
} from '../types';

// Import des données
import resistanceParoisData from '../resources/resistance-parois.json';
import resistanceIsolantData from '../resources/resistance-isolant.json';
import rendementChauffageData from '../resources/rendement-chauffage.json';
import coutTravauxData from '../resources/cout-travaux.json';

// Typage des données importées avec assertion de type
export const resistanceParois = resistanceParoisData.donnees as ResistanceParoi[];
export const resistanceIsolant = resistanceIsolantData.donnees as ResistanceIsolant[];
export const rendementChauffage = rendementChauffageData.donnees as RendementChauffage[];
export const coutTravaux = coutTravauxData.donnees as CoutTravaux[];

// Mise à jour du type CoutTravaux pour correspondre aux données
export interface CoutTravaux {
  type: string;  // isolationMurs, isolationToiture, etc.
  cout: number;  // €/m²
}

// Définir les coefficients de transmission thermique (U) pour les différents matériaux de murs
const coefficientTransmissionMurs: Record<string, number> = {
  'beton': 2.5,  // W/m²K
  'brique': 2.0, // W/m²K
  'pierre': 2.3  // W/m²K
};

// Définir les DJU mensuels
const djuMensuels: Record<string, number> = {
  janvier: 460,
  fevrier: 394,
  mars: 322,
  avril: 218,
  mai: 107,
  juin: 35,
  juillet: 7,
  aout: 12,
  septembre: 71,
  octobre: 195,
  novembre: 340,
  decembre: 442
};

// Types pour les fonctions utilitaires
type ParoiType = 'mur' | 'toit' | 'plancher' | 'fenetre';

interface FinancialAdvice {
  type: 'excellent' | 'bon' | 'moyen' | 'long';
  color: string;
  message: string;
}

// Fonctions utilitaires avec types corrects
const formatNomMateriau = (type: TypeIsolant): string => {
  const noms: Record<TypeIsolant, string> = {
    '': '',
    'laineVerre': 'laine de verre',
    'laineRoche': 'laine de roche',
    'polystyrene': 'polystyrène',
    'polyurethane': 'polyuréthane',
    'fibreBois': 'fibre de bois',
    'chanvre': 'chanvre'
  };
  return noms[type] || type;
};

const formatNomComposant = (composant: TypeComposant): string => {
  const noms: Record<TypeComposant, string> = {
    'murs': 'des murs',
    'toiture': 'de la toiture',
    'plancher': 'du plancher',
    'fenetres': 'des fenêtres',
    'chauffage': 'du système de chauffage',
    'ecs': 'du système d\'eau chaude sanitaire'
  };
  return noms[composant];
};

const getCoutUnitaire = (composant: TypeComposant, typeTravaux: TypeTravaux): number => {
  let typeRecherche = '';
  
  switch(typeTravaux) {
    case 'isolation':
      typeRecherche = `isolation${composant.charAt(0).toUpperCase() + composant.slice(1)}`;
      break;
    case 'remplacement':
      typeRecherche = 'remplacementFenetres';
      break;
    case 'pac':
      typeRecherche = 'installationPac';
      break;
  }

  const coutParType = coutTravaux.find(c => c.type === typeRecherche);
  if (!coutParType) {
    console.warn(`Coût non trouvé pour ${typeRecherche}`);
    return 0;
  }
  return coutParType.cout;
};

const getResistanceParoi = (type: string): number => {
  const paroi = resistanceParois.find(p => p.type === type);
  return paroi?.resistance || 0;
};

const getResistanceIsolant = (type: TypeIsolant): number => {
  const isolant = resistanceIsolant.find(i => i.type === type);
  return isolant?.resistance || 0;
};

const getRendementChauffage = (type: string): number => {
  const chauffage = rendementChauffage.find(c => c.type === type);
  return chauffage?.rendement || 0.7;
};

const trouverMeilleurIsolant = (composant: TypeComposant): TypeIsolant => {
  const isolantsRecommandes: Record<TypeComposant, TypeIsolant[]> = {
    'murs': ['laineRoche', 'laineVerre', 'polystyrene'],
    'toiture': ['laineVerre', 'laineRoche', 'fibreBois'],
    'plancher': ['polystyrene', 'polyurethane', 'laineRoche'],
    'fenetres': [], // Pas d'isolation pour les fenêtres
    'chauffage': [], // Pas d'isolation pour le chauffage
    'ecs': [] // Pas d'isolation pour l'ECS
  };

  const isolantsPossibles = resistanceIsolant.filter(isolant => 
    isolantsRecommandes[composant]?.includes(isolant.type)
  );

  if (!isolantsPossibles.length) return 'laineVerre';

  const isolantsAvecCout = isolantsPossibles.map(isolant => ({
    type: isolant.type,
    ratio: isolant.resistance / (getCoutUnitaire(composant, 'isolation') || 1)
  }));

  return isolantsAvecCout.sort((a, b) => b.ratio - a.ratio)[0].type;
};

interface DeperditionsResult {
  total: number;
  parComposant: {
    murs: number;
    toiture: number;
    plancher: number;
    fenetres: number;
  };
}

export const calculateDeperditions = (formData: FormDataInterface): DeperditionsResult => {
  // Calcul des déperditions pour chaque composant
  const deperditionsMurs = calculateParoiDeperditions(
    formData.murs.surface,
    'mur',
    formData.murs.isolation?.type || null,
    formData.murs.type
  );

  const deperditionsToiture = calculateParoiDeperditions(
    formData.toiture.surface,
    'toit',
    formData.toiture.isolation?.type || null
  );

  const deperditionsPlancher = calculateParoiDeperditions(
    formData.plancher.surface,
    'plancher',
    formData.plancher.isolation?.type || null
  );

  const deperditionsFenetres = calculateFenetresDeperditions(
    formData.fenetres.surface,
    formData.fenetres.type
  );

  const deperditionsTotal = 
    deperditionsMurs +
    deperditionsToiture +
    deperditionsPlancher +
    deperditionsFenetres;

  return {
    total: deperditionsTotal,
    parComposant: {
      murs: deperditionsMurs,
      toiture: deperditionsToiture,
      plancher: deperditionsPlancher,
      fenetres: deperditionsFenetres
    }
  };
};

const calculateParoiDeperditions = (
  surface: number,
  type: ParoiType,
  isolationType: TypeIsolant | null = null,
  materiauMur: string | null = null
): number => {
  if (!surface || surface <= 0) return 0;
  
  let resistance = 0;

  if (type === 'mur') {
    const coefficientMur = materiauMur && coefficientTransmissionMurs[materiauMur] 
      ? coefficientTransmissionMurs[materiauMur] 
      : coefficientTransmissionMurs['beton'];
    resistance = 1 / coefficientMur;

    if (isolationType) {
      const isolant = resistanceIsolant.find(i => i.type === isolationType);
      if (isolant?.resistance && isolant.resistance > 0) {
        resistance += isolant.resistance;
      }
    }
  } else {
    resistance = type === 'toit' ? 0.1 : type === 'plancher' ? 0.15 : 0.2;
    
    if (isolationType) {
      const isolant = resistanceIsolant.find(i => i.type === isolationType);
      if (isolant?.resistance && isolant.resistance > 0) {
        resistance += isolant.resistance;
      }
    }
  }

  if (resistance <= 0) {
    console.warn(`Résistance invalide pour ${type}:`, resistance);
    resistance = 0.1;
  }

  const u = 1 / resistance;
  return Math.max(0, u * surface);
};

const calculateFenetresDeperditions = (
  surface: number,
  typeVitrage: TypeVitrage
): number => {
  if (!surface || surface <= 0) return 0;
  
  const resistanceVitrage = getResistanceParoi(typeVitrage);
  if (resistanceVitrage <= 0) return 0;
  
  const conductivite = 1 / resistanceVitrage;
  return surface * conductivite;
};

export const calculateConsommation = (
  deperditions: number,
  temperatureInterieure: number,
  temperatureExterieure: number,
  typeChauffage: string,
  hasVeilleuse: boolean = false
): number => {
  if (!isFinite(deperditions) || deperditions <= 0) {
    console.warn('Déperditions invalides:', deperditions);
    return 0;
  }

  const rendement = getRendementChauffage(typeChauffage);
  if (rendement <= 0) {
    console.warn('Rendement invalide pour le type de chauffage:', typeChauffage);
    return 0;
  }

  const deltaTref = Math.max(0, temperatureInterieure - temperatureExterieure);
  if (deltaTref <= 0) {
    console.warn('Différence de température invalide:', deltaTref);
    return 0;
  }

  const facteurCorrection = deltaTref / 12;
  const djuAnnuel = Object.values(djuMensuels).reduce((sum, dju) => sum + dju, 0);

  // Ajout de la consommation de la veilleuse (environ 100W en continu)
  const consommationVeilleuse = hasVeilleuse ? (0.1 * 24 * 365) : 0;

  const consommationChauffage = (deperditions * 24 * djuAnnuel * facteurCorrection) / (rendement * 1000);
  return Math.max(0, Math.min(consommationChauffage + consommationVeilleuse, 1000000));
};

export const estimerPuissanceChauffage = (deperditionsTotal: number): number => {
  if (!isFinite(deperditionsTotal) || deperditionsTotal <= 0) {
    console.warn('Déperditions totales invalides:', deperditionsTotal);
    return 0;
  }
  
  // Marge de sécurité de 20%
  return deperditionsTotal * 1.2;
};

export const analyserTravauxPotentiels = (
  formData: FormDataInterface,
  deperditions: DeperditionsResult,
  consommation: number
): RecommandationTravaux[] => {
  const recommandations: RecommandationTravaux[] = [];
  const prixEnergie = 0.174; // €/kWh

  // Analyse des parois
  for (const composant of ['murs', 'toiture', 'plancher'] as const) {
    const paroi = formData[composant];
    const deperditionActuelle = deperditions.parComposant[composant];

    if (!paroi.isolation?.type || !paroi.isolation) {
      const meilleurIsolant = trouverMeilleurIsolant(composant);
      const economieEnergie = calculerEconomieIsolation(
        deperditionActuelle,
        paroi.surface,
        meilleurIsolant,
        composantToParoiType(composant),
        composant === 'murs' ? (paroi as ParoiData & { type: TypeMur }).type : undefined
      );

      const coutEstime = estimerCoutTravaux(composant, 'isolation', paroi.surface);
      const tempsRetour = calculerTempsRetour(economieEnergie, coutEstime, prixEnergie);

      recommandations.push({
        composant,
        travaux: `Isolation ${formatNomComposant(composant)} en ${formatNomMateriau(meilleurIsolant)}`,
        economieEnergie,
        coutEstime,
        tempsRetour,
        priorite: deperditionActuelle / deperditions.total > 0.25 ? 1 : 2
      });
    }
  }

  // Fenêtres simple vitrage
  if (formData.fenetres.type === 'simple') {
    const deperditionActuelle = deperditions.parComposant.fenetres;
    const deperditionApres = calculateFenetresDeperditions(
      formData.fenetres.surface,
      'double'
    );
    
    const economieEnergie = calculerEconomieChangementFenetres(
      deperditionActuelle,
      'double'
    );

    const coutEstime = estimerCoutTravaux('fenetres', 'remplacement', formData.fenetres.surface);
    const tempsRetour = calculerTempsRetour(economieEnergie, coutEstime, prixEnergie);

    recommandations.push({
      composant: 'fenetres',
      travaux: 'Remplacement par du double vitrage',
      economieEnergie,
      coutEstime,
      tempsRetour,
      priorite: deperditionActuelle / deperditions.total > 0.15 ? 1 : 2
    });
  }

  // Chauffage ancien
  if (['gazAncien', 'fioulAncien'].includes(formData.chauffage.type)) {
    const economieEnergie = calculerEconomieChangementChauffage(
      consommation,
      formData.chauffage.type,
      'pac'
    );

    const coutEstime = estimerCoutTravaux('chauffage', 'pac', 1);
    const tempsRetour = calculerTempsRetour(economieEnergie, coutEstime, prixEnergie);

    recommandations.push({
      composant: 'chauffage',
      travaux: 'Installation d\'une pompe à chaleur',
      economieEnergie,
      coutEstime,
      tempsRetour,
      priorite: 1
    });
  }

  // Chauffage ancien avec veilleuse
  if (formData.chauffage.hasVeilleuse) {
    const economieEnergie = 876; // 0.1kW * 24h * 365j = 876 kWh/an
    const coutEstime = 250; // Coût moyen pour remplacer une veilleuse par un allumage électronique
    const tempsRetour = calculerTempsRetour(economieEnergie, coutEstime, prixEnergie);

    recommandations.push({
      composant: 'chauffage',
      travaux: 'Remplacement de la veilleuse par un allumage électronique',
      economieEnergie,
      coutEstime,
      tempsRetour,
      priorite: 1
    });
  }

  // ECS ancien ou peu performant
  if (formData.ecs?.rendement && formData.ecs.rendement < 85) {
    let nouveauType = '';
    let economieEnergie = 0;
    let coutEstime = 0;

    if (formData.ecs.type === 'ballonElectrique' && formData.ecs.puissance) {
      nouveauType = 'ballonThermodynamique';
      economieEnergie = calculerEconomieChangementEcs(
        formData.ecs.puissance,
        formData.ecs.rendement,
        95 // Rendement moyen d'un ballon thermodynamique
      );
      coutEstime = 2500; // Coût moyen d'un ballon thermodynamique
    } else if (formData.ecs.type === 'chaudiereDouble' && formData.ecs.puissance) {
      nouveauType = 'chauffeEauSolaire';
      economieEnergie = calculerEconomieChangementEcs(
        formData.ecs.puissance,
        formData.ecs.rendement,
        98 // Rendement moyen d'un chauffe-eau solaire
      );
      coutEstime = 4000; // Coût moyen d'un chauffe-eau solaire
    }

    if (nouveauType) {
      const tempsRetour = calculerTempsRetour(economieEnergie, coutEstime, prixEnergie);
      recommandations.push({
        composant: 'ecs',
        travaux: `Remplacement par un ${nouveauType}`,
        economieEnergie,
        coutEstime,
        tempsRetour,
        priorite: 2
      });
    }
  }

  return recommandations.sort((a, b) => {
    if (a.priorite === b.priorite) {
      return a.tempsRetour - b.tempsRetour;
    }
    return a.priorite - b.priorite;
  });
};

const calculerEconomieIsolation = (
  deperditionActuelle: number,
  surface: number,
  typeIsolant: TypeIsolant,
  typeParoi: ParoiType,
  materiauMur?: string
): number => {
  const deperditionApres = calculateParoiDeperditions(
    surface,
    typeParoi,
    typeIsolant,
    materiauMur
  );

  const reductionDeperdition = Math.max(0, deperditionActuelle - deperditionApres);
  const djuAnnuel = Object.values(djuMensuels).reduce((sum, dju) => sum + dju, 0);
  
  return (reductionDeperdition * 24 * djuAnnuel) / 1000;
};

const calculerEconomieChangementFenetres = (
  deperditionActuelle: number,
  nouveauType: TypeVitrage
): number => {
  const resistanceActuelle = getResistanceParoi('simple');
  const resistanceNouvelle = getResistanceParoi(nouveauType);
  
  if (resistanceActuelle <= 0 || resistanceNouvelle <= 0) {
    console.warn('Résistances thermiques invalides pour les fenêtres');
    return 0;
  }
  
  const reduction = deperditionActuelle * (1 - (resistanceActuelle / resistanceNouvelle));
  const djuAnnuel = Object.values(djuMensuels).reduce((sum, dju) => sum + dju, 0);
  
  return Math.max(0, (reduction * 24 * djuAnnuel) / 1000);
};

const calculerEconomieChangementChauffage = (
  consommationActuelle: number,
  typeChauffageActuel: string,
  nouveauType: string
): number => {
  const rendementActuel = getRendementChauffage(typeChauffageActuel);
  const rendementNouveau = getRendementChauffage(nouveauType);
  
  if (rendementActuel <= 0 || rendementNouveau <= 0) {
    console.warn('Rendements de chauffage invalides');
    return 0;
  }
  
  return Math.max(0, consommationActuelle * (1 - rendementActuel / rendementNouveau));
};

const calculerEconomieChangementEcs = (
  puissance: number,
  rendementActuel: number,
  rendementNouveau: number
): number => {
  // Estimation de la consommation annuelle d'ECS basée sur la puissance
  const heuresUtilisationJour = 2; // Estimation moyenne
  const consommationAnnuelle = (puissance * heuresUtilisationJour * 365) / rendementActuel;
  
  // Calcul de l'économie avec le nouveau rendement
  const nouvelleConsommation = (puissance * heuresUtilisationJour * 365) / rendementNouveau;
  
  return Math.max(0, consommationAnnuelle - nouvelleConsommation);
};

const estimerCoutTravaux = (
  composant: TypeComposant,
  typeTravaux: TypeTravaux,
  surface: number
): number => {
  const coutUnitaire = getCoutUnitaire(composant, typeTravaux);
  
  // Pour la PAC, la surface n'est pas pertinente, on utilise le coût forfaitaire
  if (typeTravaux === 'pac') {
    return coutUnitaire;
  }
  
  return Math.max(0, surface * coutUnitaire);
};

const calculerTempsRetour = (
  economieEnergie: number,
  coutTravaux: number,
  prixEnergie: number = 0.174
): number => {
  const economieAnnuelle = economieEnergie * prixEnergie;
  
  if (economieAnnuelle <= 0) {
    console.warn('Économie annuelle nulle ou négative');
    return Infinity;
  }
  
  return coutTravaux / economieAnnuelle;
};

const composantToParoiType = (composant: TypeComposant): ParoiType => {
  const mapping: Record<TypeComposant, ParoiType> = {
    'murs': 'mur',
    'toiture': 'toit',
    'plancher': 'plancher',
    'fenetres': 'fenetre',
    'chauffage': 'mur', // Valeur par défaut, ne devrait jamais être utilisé
    'ecs': 'mur' // Valeur par défaut, ne devrait jamais être utilisé
  };
  return mapping[composant];
};